import { BaseAuthProvider, type BaseAuthProviderConfig } from "./BaseAuthProvider";
import { createPkcePair, randomString } from "../utils/pkce";
import { AuthHttpError, type Credentials, type Tokens, type User } from "../types";

export interface OAuthEndpoints {
  authorize: string;
  token: string;
  userInfo?: string;
  endSession?: string;
}

export interface OAuthProviderConfig extends BaseAuthProviderConfig {
  endpoints: OAuthEndpoints;
  clientId: string;
  redirectUri: string;
  scope?: string;
  audience?: string;
  postLogoutRedirectUri?: string;
  extraAuthorizeParams?: Record<string, string>;
}

interface RawTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  id_token?: string;
}

/**
 * Authorization Code + PKCE flow, suitable for any OAuth2/OIDC-compliant
 * identity provider. `login()` redirects the browser; the resulting
 * `code` is exchanged for tokens on the next `initialize()` call, which
 * every app is expected to run once on boot inside `<AuthProvider>`.
 */
export class OAuthProvider extends BaseAuthProvider {
  private readonly pkceKey: string;

  constructor(protected readonly config: OAuthProviderConfig) {
    super(config);
    this.pkceKey = `ferrumec.oauth.pkce.${config.clientId}`;
  }

  async initialize(): Promise<void> {
    const callbackTokens = await this.tryHandleRedirectCallback();
    if (callbackTokens) {
      this.session.setTokens(callbackTokens);
      const user = await this.doFetchCurrentUser();
      this.setCurrentUser(user);
      this.session.startIdleWatch();
      if (user) this.events.emit("login", { user });
      this.cleanUpCallbackUrl();
      return;
    }
    await super.initialize();
  }

  /** Redirects the browser to the identity provider. This promise never resolves — the page navigates away. */
  async login(_credentials?: Credentials): Promise<void> {
    if (typeof window === "undefined") throw new Error("OAuthProvider.login() requires a browser environment");

    const { verifier, challenge } = await createPkcePair();
    const state = randomString(16);
    window.sessionStorage.setItem(this.pkceKey, JSON.stringify({ verifier, state }));

    const url = new URL(this.config.endpoints.authorize);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", this.config.clientId);
    url.searchParams.set("redirect_uri", this.config.redirectUri);
    url.searchParams.set("scope", this.config.scope ?? "openid profile email");
    url.searchParams.set("state", state);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", "S256");
    if (this.config.audience) url.searchParams.set("audience", this.config.audience);
    for (const [key, value] of Object.entries(this.config.extraAuthorizeParams ?? {})) {
      url.searchParams.set(key, value);
    }

    window.location.assign(url.toString());
    await new Promise<void>(() => {
      /* intentionally never resolves — the browser is navigating away */
    });
  }

  protected async doLogin(): Promise<{ tokens: Tokens; user?: User | null }> {
    throw new Error("OAuthProvider redirects for login; doLogin() is not invoked directly.");
  }

  protected async doRefresh(): Promise<Tokens> {
    const current = this.session.getTokens();
    if (!current?.refreshToken) throw new Error("No refresh token available for this session");

    const body = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: current.refreshToken,
      client_id: this.config.clientId,
    });
    const response = await fetch(this.config.endpoints.token, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!response.ok) {
      throw new AuthHttpError("Token refresh failed", { status: response.status, code: "REFRESH_FAILED" });
    }
    return this.toTokens(await response.json());
  }

  protected async doFetchCurrentUser(): Promise<User | null> {
    if (!this.config.endpoints.userInfo) return null;
    const token = await this.getAccessToken();
    if (!token) return null;

    const response = await fetch(this.config.endpoints.userInfo, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return null;
    return this.normalizeUser(await response.json());
  }

  protected async doLogout(): Promise<void> {
    if (!this.config.endpoints.endSession || typeof window === "undefined") return;
    const url = new URL(this.config.endpoints.endSession);
    url.searchParams.set("client_id", this.config.clientId);
    if (this.config.postLogoutRedirectUri) {
      url.searchParams.set("post_logout_redirect_uri", this.config.postLogoutRedirectUri);
    }
    window.location.assign(url.toString());
  }

  /** Subclasses (Auth0, Keycloak, Azure AD) can override to map provider-specific userinfo claims. */
  protected normalizeUser(raw: Record<string, unknown>): User {
    return {
      id: String(raw.sub ?? raw.id ?? raw.oid ?? ""),
      email: typeof raw.email === "string" ? raw.email : undefined,
      username: typeof raw.preferred_username === "string" ? raw.preferred_username : undefined,
      displayName: typeof raw.name === "string" ? raw.name : undefined,
      avatarUrl: typeof raw.picture === "string" ? raw.picture : undefined,
      ...raw,
    };
  }

  private toTokens(raw: RawTokenResponse): Tokens {
    return {
      accessToken: raw.access_token,
      refreshToken: raw.refresh_token ?? this.session.getTokens()?.refreshToken,
      expiresAt: raw.expires_in !== undefined ? Date.now() + raw.expires_in * 1000 : undefined,
    };
  }

  private async tryHandleRedirectCallback(): Promise<Tokens | null> {
    if (typeof window === "undefined") return null;
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    if (!code || !state) return null;

    const stored = window.sessionStorage.getItem(this.pkceKey);
    if (!stored) return null;
    window.sessionStorage.removeItem(this.pkceKey);

    const { verifier, state: expectedState } = JSON.parse(stored) as { verifier: string; state: string };
    if (state !== expectedState) {
      throw new Error("OAuth `state` mismatch on callback — possible CSRF, aborting token exchange");
    }

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: this.config.redirectUri,
      client_id: this.config.clientId,
      code_verifier: verifier,
    });
    const response = await fetch(this.config.endpoints.token, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!response.ok) {
      throw new AuthHttpError("Authorization code exchange failed", {
        status: response.status,
        code: "TOKEN_EXCHANGE_FAILED",
      });
    }
    return this.toTokens(await response.json());
  }

  private cleanUpCallbackUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    window.history.replaceState({}, "", url.toString());
  }
}
